import { useState } from "react"
import { z } from "zod"
import { useForm, zodResolver } from "@mantine/form"
import {
  Text,
  TextInput,
  Button,
  Group,
  Textarea,
  LoadingOverlay,
  useMantineTheme,
  MultiSelect,
} from "@mantine/core"
import { Dropzone, IMAGE_MIME_TYPE } from "@mantine/dropzone"
import { DateRangePicker } from "@mantine/dates"
import { Icon } from "@material-tailwind/react"
import useFetchAndLoad from "hooks/useFetchAndLoad.ts"
import { create, update } from "services/api.service"
import { dropzoneChildren } from "./ImageDropzoneUtils"

const schema = z.object({
  name: z.string().min(1, { message: "Nombre debe tener al menos 1 letra" }),
  lastName_1: z.string().min(1, { message: "Primer apellido debe tener al menos 1 letra" }),
  lastName_2: z.string().min(1, { message: "Segundo apellido debe tener al menos 1 letra" }),
  datesRange: z
    .array(z.date())
    .min(2, { message: "Debe seleccionar un rango de fechas de acceso" }),
  worksites: z.array(z.string()).nonempty({ message: "Debes seleccionar al menos una obra" }),
})

const ContractorEmployeeForm = ({
  data,
  refresh,
  isCreation,
  notifications,
  endpoint,
  contractor,
}) => {
  const { loading, callEndpoint } = useFetchAndLoad()
  const theme = useMantineTheme()
  const responseWorksites = localStorage.getItem("worksites")
  const worksitesSelectData = JSON.parse(responseWorksites).map((worksite) => ({
    value: worksite._id,
    label: worksite.name,
  }))

  const form = useForm({
    schema: zodResolver(schema),
    initialValues: {
      name: data.first_name,
      lastName_1: data.last_name_1,
      lastName_2: data.last_name_2,
      work: data.work,
      datesRange:
        data.start_date && data.end_date
          ? [new Date(data.start_date), new Date(data.end_date)]
          : [],
      comment: data.comment,
      file: data.image,
      contractorName: contractor.name,
      worksites: data.worksites,
    },
  })

  const datesRange = form.getInputProps("datesRange").value
    ? [
        new Date(form.getInputProps("datesRange").value[0]),
        new Date(form.getInputProps("datesRange").value[1]),
      ]
    : []

  const [worksitesDefault, setWorksites] = useState(data.worksites)

  const handleSubmit = async (values) => {
    const { name, lastName_1, lastName_2, work, comment, file, datesRange, worksites } = values
    const employee = {
      ...data,
      first_name: name,
      last_name_1: lastName_1,
      last_name_2: lastName_2,
      start_date: datesRange[0],
      end_date: datesRange[1],
      work: work ? work : "",
      comment: comment ? comment : "",
      file: file,
      contractor: contractor._id,
      worksites: worksites,
    }

    delete employee.name

    if (data._id) {
      await callEndpoint(update(endpoint, employee))
        .then((response) => {
          if (response.status === 200 || response.status === 201) {
            refresh()
            notifications.showNotification({
              color: "green",
              title: "Empleado actualizado",
              message: "El empleado ha sido actualizado con éxito.",
              icon: <Icon name={"check"} />,
            })
          }
        })
        .catch((error) => {
          showErrorNotification()
        })

      return
    }

    await callEndpoint(create(endpoint, employee))
      .then((response) => {
        if (response.status === 200 || response.status === 201) {
          refresh()
          notifications.showNotification({
            color: "green",
            title: "Empleado creado",
            message: "El empleado ha sido creado con éxito.",
            icon: <Icon name={"check"} />,
          })
        }
      })
      .catch((error) => {
        showErrorNotification()
      })
  }

  const showErrorNotification = () => {
    notifications.showNotification({
      color: "red",
      title: "Error, por favor intentelo nuevamente.",
      message: "Si el problema persiste contactar a soporte.",
      icon: <Icon name={"close"} />,
    })
  }

  return (
    <>
      {loading && <LoadingOverlay visible={true} />}
      <form
        onSubmit={form.onSubmit(async (values) => {
          await handleSubmit(values)
        })}>
        <TextInput
          disabled={true}
          label='Contratista'
          placeholder='Contratista'
          {...form.getInputProps("contractorName")}
        />
        <TextInput
          required
          label='Nombre(s)'
          placeholder='Nombre(s)'
          {...form.getInputProps("name")}
        />
        <TextInput
          required
          label='Apellido Paterno'
          placeholder='Apellido Paterno'
          mt='sm'
          {...form.getInputProps("lastName_1")}
        />
        <TextInput
          required
          label='Apellido Materno'
          placeholder='Apellido Materno'
          mt='sm'
          {...form.getInputProps("lastName_2")}
        />

        <DateRangePicker
          mt='sm'
          required
          label='Fecha de inicio a fin'
          placeholder='Elige un rango de fechas'
          defaultValue={datesRange[0] > 1 && datesRange[1] > 1 ? datesRange : []}
          error={
            form.getInputProps("datesRange").value.length > 0
              ? form.getInputProps("datesRange").value[1] > 1
                ? false
                : true
              : false
          }
          {...form.getInputProps("datesRange")}
        />

        <MultiSelect
          mt='sm'
          defaultValue={worksitesDefault}
          data={worksitesSelectData}
          label='Obras'
          placeholder='Selecciona las obras a las que tendrá acceso el empleado'
          onChange={setWorksites}
          clearable
          required
          {...form.getInputProps("worksites")}
        />

        <TextInput label='Trabajo' placeholder='Trabajo' mt='sm' {...form.getInputProps("work")} />

        <Textarea
          mt='sm'
          placeholder='Comentario'
          label='Escribe aquí tu comentario'
          autosize
          minRows={2}
          {...form.getInputProps("comment")}
        />

        <>
          <Text size='sm' inline weight={500} mt='lg'>
            Fotografía
          </Text>
          <Dropzone
            mt='sm'
            onDrop={(files) => {
              form.setFieldValue("file", files[0])
            }}
            onReject={() =>
              notifications.showNotification({
                color: "red",
                title: "Archivo no válido.",
                message: "El archivo es demasiado grande o el formato no es compatible.",
                icon: <Icon name={"close"} />,
              })
            }
            maxSize={3 * 1024 ** 2}
            accept={IMAGE_MIME_TYPE}>
            {(status) => dropzoneChildren(status, theme, form.getInputProps("file"))}
          </Dropzone>
        </>

        <Group position='center' mt='xl'>
          <Button className='bg-gray-700 text-white hover:bg-gray-600' type='submit'>
            {isCreation ? "Crear Empleado" : "Actualizar Empleado"}
          </Button>
        </Group>
      </form>
    </>
  )
}

export default ContractorEmployeeForm
